import preact, { Component, h } from 'preact'
import DropdownControl from '../dropdown/DropdownControl'
import Icon from '../assets'
import classNames from 'classnames'

export const UsernameVariableNode : string = "<input-variable contenteditable=false type='username'>username</input-variable>&nbsp;"

type Props = {
  customClass?: string,
  value: string,
  onChange: (value: string) => void,
  maxLength?: number,
  error?: boolean
}

type State = {
  cursorPosition: Range | null,
  isFocused: boolean
}

class EditableInputWithVariable extends Component<Props, State> {
  private contentEditable : HTMLElement = preact.createRef()
  private lastHtml : string | undefined

  constructor () {
    super()
    this.state = {
      cursorPosition: null,
      isFocused: false
    }
  }

  onKeyDown = (e: KeyboardEvent) => {
    // prevents line breaks
    if (e.key === 'Enter') {
      e.preventDefault()
    }

    if (window.getSelection && e.key == 'Backspace') { // backspace
      // fix backspace bug in FF
      // https://bugzilla.mozilla.org/show_bug.cgi?id=685445
      const selection = window.getSelection()

      if (!selection?.isCollapsed || !selection.rangeCount) {
        return
      }

      const curRange = selection.getRangeAt(selection.rangeCount - 1)
      if (curRange.commonAncestorContainer.nodeType == 3 && curRange.startOffset > 0) {
        // we are in child selection. The characters of the text node is being deleted
        return
      }

      const range = document.createRange()
      if (selection.anchorNode !== this.contentEditable) {
        // selection is in character mode. expand it to the whole editable field
        range.selectNodeContents(this.contentEditable)
        if(selection.anchorNode) {
          range.setEndBefore(selection.anchorNode)
        }
      } else if (selection.anchorOffset > 0) {
        range.setEnd(this.contentEditable, selection.anchorOffset)
      } else {
        // reached the beginning of editable field
        return
      }

      range.setStart(this.contentEditable, range.endOffset - 1)

      const previousNode = range.cloneContents().lastChild
      if (previousNode) {
        // this is some rich content, e.g. smile. We should help the user to delete it
        range.deleteContents();
        e.preventDefault();
      }
    }

    this.emitChange()
  }

  saveCursorPosition = () => {
    const selection = window.getSelection()

    if (selection?.rangeCount) {
      this.setState({
        cursorPosition: selection.getRangeAt(0)
      })
    }
  }

  inserVariable = (node: string) => {
    const { cursorPosition } = this.state

    if (!cursorPosition) return

    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(cursorPosition)

    cursorPosition.deleteContents()
    cursorPosition.insertNode(cursorPosition.createContextualFragment(node))

    cursorPosition.collapse(false)

    this.emitChange()
  }

  emitChange = () => {
    const { innerHTML } = this.contentEditable

    if (this.props.onChange && innerHTML !== this.lastHtml) {
      this.props.onChange(innerHTML)
    }

    this.lastHtml = innerHTML
  }

  shouldComponentUpdate = (nextProps: Readonly<Props>): boolean => {
    return nextProps.value !== this.contentEditable.innerHTML
  }

  componentDidUpdate = () => {
    const { value } = this.props
    const { innerHTML } = this.contentEditable

    if (value !== innerHTML) {
      this.contentEditable.innerHTML = value
    }
  }

  render ({ customClass, value, onChange, maxLength, error } : Props, { isFocused } : State) {
    return <div class='editable-input-with-variable'>
      <div class='input-wrapper'>
        <div
          ref={(ref) => this.contentEditable = ref}
          contentEditable
          onInput={this.emitChange}
          onBlur={() => this.saveCursorPosition()}
          onKeyDown={this.onKeyDown}
          onKeyPress={(e) => {
            if(!maxLength) return
            if(this.contentEditable.innerText.length >= maxLength) {
              e.preventDefault()
            }
          }}
          onPaste={(e) => {
            if(!maxLength) return

            let paste = (e.clipboardData || (window as any).clipboardData).getData('text');
            let currentText = this.contentEditable.innerText

            if((currentText.length + paste.length) >= maxLength) {
              e.preventDefault()
            }
          }}
          class={classNames(customClass, 'content-editable', { error: error })}
          dangerouslySetInnerHTML={{ __html: value }} />
        <DropdownControl
          newStyle
          triggerClass={classNames('variable-dropdown', { focused: isFocused })}
          align='right'
          trigger={<Icon icon='variable' />}
          items={[
            {
              groupName: 'Recipient attributes',
              items: [
                {
                  title: 'User name',
                  value: 'username',
                  onClick: () => this.inserVariable(UsernameVariableNode)
                }
              ]
            }
          ]}
        />
      </div>
    </div>
  }
}

export default EditableInputWithVariable
